import useAnchorClickMessageDispatcher from '@carvana/merch-native/useAnchorClickMessageDispatcher';
import { useEffect, useState } from 'react';
import useMicroFrontend from '~/components/common/MicroFrontEnds';
import { isNonProd } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useCorrelationId } from '~/context/common/GlobalLayoutContext';
import isChunkLoadError from '~/utilities/isChunkLoadError';
import { logError, logInfo } from '~/utilities/loggers';
import {
  loadItemFromSessionStorage,
  saveItemToSessionStorage
} from '~/utilities/sessionStorage';

const CHUNK_LOAD_ERROR_RELOAD_KEY = 'chunk-load-error-reload';
const RELOAD_COOLDOWN_MILLISECONDS = 30_000;

// Reloads at most once per cooldown, and only when the timestamp can be written and read back,
// so a chunk that keeps failing can't loop when sessionStorage is unavailable.
function claimChunkLoadErrorReload() {
  const now = Date.now();
  const lastReloadedAt = loadItemFromSessionStorage<number>(
    CHUNK_LOAD_ERROR_RELOAD_KEY
  );

  if (lastReloadedAt && now - lastReloadedAt <= RELOAD_COOLDOWN_MILLISECONDS) {
    return false;
  }

  return (
    saveItemToSessionStorage(CHUNK_LOAD_ERROR_RELOAD_KEY, now) &&
    loadItemFromSessionStorage<number>(CHUNK_LOAD_ERROR_RELOAD_KEY) === now
  );
}

/**
 * Shared functionality for app router error pages.
 * Returns true when the error is a chunk load error that is being recovered with a reload,
 * so the caller can skip rendering the error UI.
 */
export default function useErrorPage(
  page: string,
  error?: Error & { digest?: string }
) {
  const correlationId = useCorrelationId();
  const trackEvent = useTrackEvent();
  const [isReloadSkipped, setIsReloadSkipped] = useState(false);
  const isReloadingForChunkLoadError =
    isChunkLoadError(error) && !isReloadSkipped;
  const location = `${page} App Router Error Boundary`;

  useMicroFrontend(false);

  useAnchorClickMessageDispatcher({
    logError,
    source: 'error-page',
    verboseLogs: isNonProd
  });

  useEffect(() => {
    if (!isReloadingForChunkLoadError) return;

    if (!claimChunkLoadErrorReload()) {
      setIsReloadSkipped(true);

      return;
    }

    logInfo('Chunk load error reload', {
      digest: error?.digest,
      location,
      message: error?.message
    });
    trackEvent('chunkLoadError', {
      digest: error?.digest,
      error: error?.message,
      page
    });
    window.location.reload();
  }, [error, isReloadingForChunkLoadError, location, page, trackEvent]);

  useEffect(() => {
    if (isReloadingForChunkLoadError) return;

    if (isChunkLoadError(error)) {
      logInfo('ChunkLoadError', {
        digest: error?.digest,
        location,
        message: error?.message
      });

      return;
    }

    logError(error, { digest: error?.digest, location });
  }, [error, isReloadingForChunkLoadError, location]);

  useEffect(() => {
    console.log('[debug] error', {
      correlationId,
      digest: error?.digest,
      message: error?.message,
      page,
      stack: error?.stack
    });
  }, [correlationId, error, page]);

  useEffect(() => {
    if (isReloadingForChunkLoadError) return;

    trackEvent('oopsPageError', {
      digest: error?.digest,
      error: error?.message,
      page
    });
  }, [error, isReloadingForChunkLoadError, page, trackEvent]);

  return isReloadingForChunkLoadError;
}
